import { createRef, CSSProperties, ReactNode, useState } from "react"
import useLocalStorage from "../hooks/useLocalStorage"
import { applyMutator, GameMutator, GameState, isGameSolved, MutationResult } from "../game/game"
import { Mutator } from "../game/mutators"
import { GameStateComp } from "./GameState"
import useResizeEffect from "../hooks/useResizeEffect"

interface StackEntry {
    gameState: GameState,
    mutator: Mutator,
    information: MutationResult
}

interface GameProps {
    initialState: GameState,
    baseKey: string|null,
    onUndo: ()=>void,
    disableInput: boolean,
    onMutatorSelected: (mutator: GameMutator, result: MutationResult)=>void,
    moves: number|null,
    onGameCompleted: ()=>void
    description: ReactNode
    style?: CSSProperties
    className?: string
    footer?: ReactNode
}

export default function Game(props:GameProps){
    const {
        initialState,
        baseKey,
        onUndo,
        disableInput,
        onMutatorSelected,
        moves,
        onGameCompleted,
        description,
        style
    } = props
    const [gameState, setGameState] = useState(initialState)
    const [stack, setStack] = useState<StackEntry[]>([])
    const [information, setInformation] = useLocalStorage<(MutationResult|null)[]>(baseKey ?  `${baseKey}--information` : null,initialState.gameMutators.map(()=>null))
    const [animating, setAnimating] = useState(false) // Disables input while animating
    const [flippingMutators, setFlippingMutators] = useState<number[]>([])
    const [memory, setMemory] = useState<Mutator[]>([])
    const gameRef = createRef<HTMLDivElement>();

    useResizeEffect(() => {
      if (gameRef.current) {
        const gameHeight = gameRef.current.scrollHeight + 5;
        const displayHeight = window.innerHeight;
        if (gameHeight > displayHeight) {
         (gameRef.current.style as any).zoom = `${(displayHeight / gameHeight) * 100}%`;
        }
      }
    });

    function undo(){
      if(stack.length > 0){
        onUndo()
      }
      setStack((currentStack) => {
        if(currentStack.length > 0){
          const newStack = currentStack.slice(0, currentStack.length - 1);
          const lastEntry = currentStack[currentStack.length - 1];
          
          setGameState(lastEntry.gameState);
          
          return newStack;
        }
        return currentStack;
      });
    }
  
    const solutionLength = gameState.solutions[0].mutators.length;
    
    
    return <GameStateComp 
    description={description}
    style={style}
    footer={props.footer}
    gameState={gameState} 
    disableInput={animating || disableInput}
    information={information}
    flippingMutators={flippingMutators}
    moves={moves}
    memory={memory}
    className={props.className}
    onMutatorSelected={(mutator)=>{
      if(mutator.used){
        return
      }
      const [newState, result] = applyMutator(gameState, mutator.mutator)
      const newStack = [...stack, {gameState, information :result, mutator: mutator.mutator}]
      setStack(newStack)
      onMutatorSelected(mutator, result)
      setGameState(newState)
      
      if(newState.mutationStack.length >= solutionLength){
        setMemory(newStack.map((entry)=>entry.mutator));
        (async ()=>{
          setAnimating(true)
          await new Promise(r=>setTimeout(r, 500))
          for(const stackItem of newStack){
            const mutationIndex = gameState.gameMutators.findIndex((m: GameMutator)=>m.mutator === stackItem.mutator)
            setFlippingMutators([mutationIndex])
            setInformation((currentInformation)=>currentInformation && currentInformation.map((prevInfo, i)=>i===mutationIndex? stackItem.information : prevInfo))
            await new Promise(r=>setTimeout(r, 450))
            setFlippingMutators([])
          }
          await new Promise(r=>setTimeout(r, 500))
          if(isGameSolved(newState.words, newState.customWords ?? [])){
            onGameCompleted();
          }else{
            for (let i = 0; i < solutionLength; i++){
              undo();
              await new Promise(r=>setTimeout(r, 250))
            }
            setAnimating(false)
          }
        }
        )();
      }
    }}
    onUndo={undo}
  /> 
  
}